import { Component, lazy, type ComponentType, type ReactNode } from 'react'
import { importWithRetry, isModuleLoadError } from '@/lib/module-load'
import styles from './ModuleRecovery.module.css'

export function LoadFailure({ module = false }: { module?: boolean }) {
  return (
    <section className={styles.notice} role="alert">
      <h2>
        {module
          ? 'Não foi possível abrir esta parte da página'
          : 'Não foi possível exibir esta página'}
      </h2>
      <p>
        {module
          ? 'A conexão pode ter falhado ou uma nova versão do site pode estar disponível. Confira sua conexão e recarregue a página para tentar novamente.'
          : 'Ocorreu um erro inesperado. Recarregue a página para tentar novamente.'}
      </p>
      <p className={styles.hint}>
        Antes de recarregar, salve as alterações que ainda estiverem abertas. Alterações
        não salvas podem ser perdidas.
      </p>
      <button type="button" onClick={() => window.location.reload()}>
        Recarregar página
      </button>
    </section>
  )
}

class ModuleBoundary extends Component<{ children: ReactNode }, { error: unknown }> {
  state: { error: unknown } = { error: null }
  static getDerivedStateFromError(error: unknown) {
    return { error }
  }
  render() {
    if (this.state.error) {
      if (!isModuleLoadError(this.state.error)) throw this.state.error
      return <LoadFailure module />
    }
    return this.props.children
  }
}

// Each lazy section owns its boundary so an unavailable download cannot unmount the form/game.
// eslint-disable-next-line react-refresh/only-export-components
export function recoverableLazy<P extends object>(
  loader: () => Promise<{ default: ComponentType<P> }>
) {
  const Loaded = lazy(() => importWithRetry(loader))
  return function RecoverableSection(props: P) {
    return (
      <ModuleBoundary>
        <Loaded {...props} />
      </ModuleBoundary>
    )
  }
}
